import React, { createContext, useContext, ReactNode } from 'react';
import { useFormData } from '@hooks/useFormData';
import { useCSVData } from '@hooks/useCSVData';
import { useConditionalQuestions } from '@hooks/useConditionalQuestions';
import type { Answer, ClientInfo } from '@/types';

interface FormContextType {
  // From useCSVData
  questions: ReturnType<typeof useCSVData>['questions'];
  allQuestions: ReturnType<typeof useCSVData>['questions']; // All questions regardless of conditions
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;

  // From useFormData
  currentStep: number;
  answers: Map<string, Answer>;
  clientInfo: ClientInfo | null;
  totalPoints: number;

  // Actions
  saveAnswer: (questionId: string, answer: Answer) => void;
  removeAnswer: (questionId: string) => void;
  saveClientInfo: (info: ClientInfo) => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (step: number) => void;
  resetForm: () => void;

  // Computed
  canGoNext: boolean;
  canGoPrevious: boolean;
  progress: number;
  getProductRecommendations: () => string[];
  getBonusProducts: () => string[];
  currentQuestion: ReturnType<typeof useCSVData>['questions'][0] | null;
}

const FormContext = createContext<FormContextType | undefined>(undefined);

export const useForm = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error('useForm must be used within FormProvider');
  }
  return context;
};

interface FormProviderProps {
  children: ReactNode;
}

export const FormProvider: React.FC<FormProviderProps> = ({ children }) => {
  const csvData = useCSVData();
  const { questions: allQuestions, loading, error, refetch } = csvData;

  // We need to use a maximum possible steps for initialization
  // This is because the actual steps will vary based on conditional questions
  const maxPossibleSteps = allQuestions.length + 2;
  const formData = useFormData(maxPossibleSteps);

  // Use conditional questions logic
  const { visibleQuestions } = useConditionalQuestions(allQuestions, formData.answers);

  // Actual total steps based on visible questions
  const actualTotalSteps = visibleQuestions.length + 2;

  // Get current question based on step and visible questions
  const currentQuestion =
    formData.currentStep > 0 && formData.currentStep <= visibleQuestions.length
      ? visibleQuestions[formData.currentStep - 1]
      : null;

  // Override computed values based on actual visible questions
  const canGoNext = formData.currentStep < actualTotalSteps;
  const canGoPrevious = formData.currentStep > 0;
  const progress = actualTotalSteps > 0 ? ((formData.currentStep + 1) / actualTotalSteps) * 100 : 0;

  const value: FormContextType = {
    // Use visible questions instead of all questions
    questions: visibleQuestions,
    allQuestions,  // Add all questions for reference
    loading,
    error,
    refetch,

    // Form data
    ...formData,
    // Override with actual computed values
    canGoNext,
    canGoPrevious,
    progress,

    // Add current question
    currentQuestion,
  };

  return <FormContext.Provider value={value}>{children}</FormContext.Provider>;
};
